/* eslint-disable no-param-reassign */
import React, { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  styled,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import {
  useGetInternalFunderReportLazyQuery,
  useGenerateInternalFunderReportLazyQuery,
  useGetRecentFunderReportsQuery,
  RecentReports,
} from 'Generated/graphql-types'
import { SuperLoading } from 'Components/SharedComponents/Loading/SuperLoading'
import { colors } from '@resiliatech/birdsong'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core'
import { ReportSection } from './ReportSection'

export const FunderReport = () => {
  const [funderOrgId, setFunderOrgId] = useState<number>(0)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [funderOrgIdGen, setFunderOrgIdGen] = useState<number>(0)
  const [targetMonth, setTargetMonth] = useState<string>('')
  const [reportData, setReportData] = useState<any>(null)
  const [skipEmail, setSkipEmail] = useState<boolean>(true)

  const [recentReports, setRecentReports] = useState<(RecentReports | null)[]>(
    []
  )

  useGetRecentFunderReportsQuery({
    nextFetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.getRecentFunderReports) {
        setRecentReports(data.getRecentFunderReports)
      }
    },
  })

  const [getFunderReport, { loading }] = useGetInternalFunderReportLazyQuery({
    variables: {
      funderOrgId,
    },
    onCompleted: (data) => {
      if (data.getInternalFunderReport?.aggregateData === false) {
        alert('No report data found for this funder')
      } else {
        const { donations } =
          data?.getInternalFunderReport?.aggregateData?.fundraising || {}
        const totalSum = (donations || []).reduce(
          (sum: number, amount: string) => {
            const numericValue = parseFloat(amount.replace('$', ''))
            return sum + numericValue
          },
          0
        )

        const funderReportData = {
          ...data.getInternalFunderReport,
          aggregateData: {
            ...data?.getInternalFunderReport?.aggregateData,
            fundraising: {
              ...data?.getInternalFunderReport?.aggregateData.fundraising,
              donations: [...donations, ...donations, ...donations],
              donationSum: totalSum.toFixed(2),
              donationCount: donations.length,
            },
          },
        }

        setReportData(funderReportData)
      }
    },
  })

  const [generateFunderReport, { loading: generating }] =
    useGenerateInternalFunderReportLazyQuery({
      variables: {
        funderOrgId,
        skipEmail,
      },
      onCompleted: (data) => {
        if (!data.generateInternalFunderReport?.generated) {
          alert('Error generating report, verify the funder id and try again')
        } else {
          // replace the existing report with the new one, if theres a funder id match, otherwise add a new report
          const existingReportIndex = recentReports.findIndex(
            (report) => report?.funderOrgId === funderOrgIdGen
          )
          // if the report already exists, replace it
          if (existingReportIndex > -1) {
            recentReports[existingReportIndex] = {
              funderOrgId: funderOrgIdGen,
              createdAt: new Date(),
              targetDate: 'Generation In Progress',
            }
            setRecentReports([...recentReports])
          } else {
            setRecentReports([
              ...recentReports,
              {
                funderOrgId: funderOrgIdGen,
                createdAt: new Date(),
                targetDate: 'Generation In Progress',
              },
            ])
          }
        }
      },
      nextFetchPolicy: 'no-cache',
    })

  const handleGetReport = () => {
    getFunderReport({
      variables: {
        funderOrgId,
      },
    })
  }

  const isFunderRecentlyGenerated = useMemo(
    () =>
      recentReports?.find((report: RecentReports | null) => {
        const reportCreatedAtUTC = new Date(report?.createdAt)
        const currentTimeUTC = new Date()

        return (
          report?.funderOrgId === funderOrgIdGen &&
          reportCreatedAtUTC.getTime() + 60000 > currentTimeUTC.getTime()
        )
      }),
    [recentReports, funderOrgIdGen]
  )

  const handleGenerateReport = () => {
    if (isFunderRecentlyGenerated) {
      return alert(
        'A report for this funder was generated recently. Please wait a few minutes before generating again, and only do so if the existing report generated incorrectly'
      )
    }
    generateFunderReport({
      variables: {
        funderOrgId: funderOrgIdGen,
        dateOverride: targetMonth || undefined,
      },
    })
    return null
  }

  return (
    <>
      <MultiSection flexWrap='nowrap'>
        <InputSection flex={1}>
          <Typography variant='h2'> Retrieve Report</Typography>
          <Typography variant='body1'>
            {' '}
            Enter the Funder ID of the report you&apos;d like to <b>VIEW</b>.
          </Typography>
          <Typography variant='body2'>
            <i>
              This will not send an email, but a preview of the email report
              will be displayed below
            </i>
          </Typography>
          <TextField
            label='Funder ID'
            type='number'
            size='small'
            onChange={(e) => {
              setFunderOrgId(parseInt(e.target.value, 10))
            }}
          />
          <Button variant='outlined' onClick={handleGetReport}>
            Get Report
          </Button>
        </InputSection>

        <InputSection flex={2}>
          <Typography variant='h2'> Generate Report</Typography>

          <Typography variant='body1'>
            {' '}
            Enter the Funder ID of the report you&apos;d like to <b>GENERATE</b>
            .
          </Typography>
          <Typography variant='body2'>
            <i>
              It will take a few minutes to generate the report. This pages does
              NOT refresh automatically so you will need to refresh to see the
              new report.
            </i>
            <br />
            <br />
            <i>
              Target Month is optional, but must be in MM/YYYY format. 06/2024
              will pull new data from 6/1/24 - 6/31-24 and the report will
              compare that against 05/2024
            </i>
          </Typography>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <TextField
              label='Funder ID'
              type='number'
              size='small'
              style={{ flex: 1 }}
              required
              onChange={(e) => {
                setFunderOrgIdGen(parseInt(e.target.value, 10))
              }}
            />
            <TextField
              label='Target Month (MM/YYYY)'
              type='string'
              style={{ flex: 1 }}
              size='small'
              aria-label='Target Month'
              value={targetMonth}
              onChange={(e) => {
                setTargetMonth(e.target.value)
              }}
            />
          </div>
          <FormControlLabel
            control={
              <Checkbox
                checked={skipEmail}
                name='skipEmail'
                onChange={() => setSkipEmail(!skipEmail)}
              />
            }
            label={
              <div>
                <div>Skip Email </div>
                <b>UNCHECKING THIS WILL SEND EMAILS TO THE FUNDER CONTACTS</b>
              </div>
            }
          />

          <Button
            variant='contained'
            onClick={handleGenerateReport}
            disabled={!funderOrgIdGen || generating}
          >
            GENERATE REPORT
          </Button>
        </InputSection>
        <InputSection style={{ padding: '0' }} flex={2}>
          <Typography variant='h2' style={{ padding: '12px 12px 0 12px ' }}>
            {' '}
            Recent Reports
          </Typography>
          <Typography variant='body2' style={{ padding: '0 12px 0 12px ' }}>
            <i>
              This table shows the 5 most recent reports by Funder ID, if you
              recently generated a report it should show up here when ready.
            </i>
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Funder ID</TableCell>
                  <TableCell>Target Date</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentReports.map((report: RecentReports | null) => (
                  <TableRow key={report?.funderOrgId}>
                    <TableCell>{report?.funderOrgId}</TableCell>
                    <TableCell>{report?.targetDate}</TableCell>
                    <TableCell>
                      {Intl.DateTimeFormat('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      }).format(new Date(report?.createdAt))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </InputSection>
      </MultiSection>

      {loading && <SuperLoading />}

      {!!reportData && (
        <>
          <Section style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant='h4'>
              Funder Report for {reportData?.reportData?.monthString}
            </Typography>

            <Button
              variant='outlined'
              style={{
                background: colors.content.aiLight,
                color: colors.content.aiDark,
                borderColor: colors.content.aiDark,
              }}
              onClick={() => {
                const detailsElements = document.querySelectorAll('details')
                detailsElements.forEach((details) => {
                  details.open = !detailsOpen
                })
                setDetailsOpen(!detailsOpen)
              }}
            >
              {' '}
              {detailsOpen ? 'Collapse' : 'Expand'} All Data
            </Button>
          </Section>

          <Section>
            <Typography variant='h5'>Aggregate Data</Typography>
            <Typography variant='body1'>
              * This is the raw data used to generate the below Claude
              summaries, and to feed into the final generated report{' '}
            </Typography>
            {reportData.aggregateData && (
              <MultiSection>
                <RenderData data={reportData.aggregateData} />
              </MultiSection>
            )}
          </Section>
          <Section>
            <Typography variant='h5'>Summary Data</Typography>
            <Typography variant='body1'>
              * This is the Claude response when asked to summarize the raw
              data. This is used in part to generate the final report
            </Typography>
            {reportData.summaryData && (
              <MultiSection>
                <RenderData data={reportData.summaryData} variant='summary' />
              </MultiSection>
            )}
          </Section>

          {reportData.reportData && (
            <ReportSection data={reportData.reportData} />
          )}
        </>
      )}
    </>
  )
}

const getSplits = (text: string) =>
  text.split('\n').map((line, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <span key={index}>
      {line}
      <br />
    </span>
  ))

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
  }).format(date)
}

const RenderData = ({
  data,
  level = 0,
  variant,
}: {
  data: any
  level?: number
  variant?: string
}) => {
  if (data === null || data === undefined) {
    return null
  }
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    const keys = Object.keys(data[0])
    return (
      <TableContainer component={Paper} style={{ minWidth: '600px' }}>
        <Table>
          <TableHead>
            <TableRow>
              {keys.map((key) => (
                <TableCell key={key}>{key}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, rowIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={rowIndex}>
                {keys.map((key) => (
                  <TableCell key={key} style={{ fontSize: '10px' }}>
                    {key === 'timestamp' ? formatTimestamp(row[key]) : row[key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }
  if (typeof data === 'object') {
    return (
      <>
        {Object.keys(data).map((key) => {
          if (level === 0) {
            return (
              <TopLevelContainer key={key} variant={variant}>
                <details>
                  <summary>{key}</summary>
                  <RenderData
                    data={data[key]}
                    level={level + 1}
                    variant={variant}
                  />
                </details>
              </TopLevelContainer>
            )
          }

          // if key contains summary
          return (
            <SubContainer
              key={key}
              variant={variant}
              sx={{ flexWrap: key === 'donations' ? 'wrap' : 'nowrap' }}
            >
              {level === 1 && <Key variant='body1'>{key}</Key>}
              <RenderData
                data={data[key]}
                level={level + 1}
                variant={variant}
              />
            </SubContainer>
          )
        })}
      </>
    )
  }
  if (typeof data === 'string' && data.includes('\n')) {
    return <Box>{getSplits(data)}</Box>
  }

  return <Box>{data}</Box>
}

const TopLevelContainer = styled(Box)<{ variant?: string }>`
  padding: 12px 8px;
  border-radius: 6px;
  background: ${colors.content.aiLight};
  flex: 1;
  max-width: ${(props) => (props.variant === 'summary' ? '' : 'fit-content')};

  summary,
  h4 {
    text-transform: capitalize;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
  }
`

const SubContainer = styled(Box)<{ variant?: string }>`
  display: flex;
  align-content: flex-start;
  padding: 2px 0;
  justify-content: space-between;
  flex-direction: ${(props) =>
    props.variant === 'summary' ? 'column' : 'row'};
  flex: ${(props) => (props.variant === 'summary' ? 1 : 1)};
`

const Key = styled(Typography)`
  margin-right: 32px;
  font-weight: 600;
  color: ${colors.content.aiDark};
`

const Section = styled(Box)`
  border: 2px solid ${colors.content.aiMedium};
  padding: 12px;
  margin-bottom: 24px;
  background: white;
  border-radius: 6px;
  gap: 10px;
`

const SubSection = styled(Box)`
  gap: 12px;
  margin: 12px 0;
`

const MultiSection = styled(SubSection)`
  gap: 12px;
  margin: 12px 0;
  display: flex;
  flex-wrap: wrap;

  td,
  th {
    padding: 6px 8px;
  }
`

const InputSection = styled(Section)`
  padding: 16px;
  display: flex;
  flex-direction: column;

  h2 {
    font-size: 22px;
    font-weight: bold;
    color: ${colors.content.aiDark};
  }

  input {
  }

  button {
    margin-top: auto;
  }

  b {
    color: ${colors.content.aiMedium};
  }

  ul {
    padding: 0;
  }
  li {
    list-style-type: none;
  }
`
